import taskmanAxios from '../helpers/axios'
import {
    API_MAKE_PROJECT_DOCUMENTS_URL,
    API_MAKE_PROJECT_DOCUMENT_URL,
    API_MAKE_PROJECT_DOCUMENT_RENAME_URL,
    API_MAKE_PROJECT_DOCUMENT_DUPLICATE_URL,
    API_MAKE_PROJECT_DOCUMENT_MOVE_URL,
    API_MAKE_PROJECT_DOCUMENT_ARCHIVE_URL,
    API_MAKE_PROJECT_DOCUMENT_UNARCHIVE_URL,
    API_MAKE_PROJECT_DOCUMENT_RESTORE_URL,
} from "../constants/api.constant";
import { ProjectDocument } from "../types";

export default class DocumentService {
    private static mergeDocuments(...documentLists: ProjectDocument[][]) {
        const documentMap = new Map<string, ProjectDocument>()

        documentLists.flat().forEach((document) => {
            if (document?.id == null) {
                return
            }

            documentMap.set(String(document.id), document)
        })

        return Array.from(documentMap.values())
    }

    private static extractDocuments(data: any): ProjectDocument[] {
        if (Array.isArray(data)) {
            return data
        }

        if (Array.isArray(data?.documents)) {
            return data.documents
        }

        if (Array.isArray(data?.items)) {
            return data.items
        }

        if (Array.isArray(data?.results)) {
            return data.results
        }

        if (Array.isArray(data?.data)) {
            return data.data
        }

        return []
    }

    static async list(projectId: string, params?: Record<string, any>): Promise<ProjectDocument[]> {
        return await taskmanAxios.get(API_MAKE_PROJECT_DOCUMENTS_URL(projectId), { params })
            .then(response => this.extractDocuments(response.data))
            .catch(e => Promise.reject(e));
    }

    static async listArchived(projectId: string): Promise<ProjectDocument[]> {
        return this.list(projectId, {
            skip: 0,
            limit: 100,
            archived_only: true,
            mine_only: false,
            trash_only: false,
        })
    }

    static async upload(projectId: string, file: File | Blob, fileName?: string) {
        const formData = new FormData()
        formData.append('file', file, fileName)

        return await taskmanAxios.post(API_MAKE_PROJECT_DOCUMENTS_URL(projectId), formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async download(projectId: string, documentId: string | number) {
        return await taskmanAxios.get(API_MAKE_PROJECT_DOCUMENT_URL(projectId, documentId), {
            responseType: 'blob'
        })
            .then(response => response)
            .catch(e => Promise.reject(e));
    }

    static async duplicate(projectId: string, documentId: string | number) {
        return await taskmanAxios.post(API_MAKE_PROJECT_DOCUMENT_DUPLICATE_URL(projectId, documentId), {})
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async rename(projectId: string, documentId: string | number, baseName: string) {
        return await taskmanAxios.patch(API_MAKE_PROJECT_DOCUMENT_RENAME_URL(projectId, documentId), { base_name: baseName })
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async move(projectId: string, documentId: string | number, data: Record<string, any>) {
        return await taskmanAxios.post(API_MAKE_PROJECT_DOCUMENT_MOVE_URL(projectId, documentId), data)
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async archive(projectId: string, documentId: string | number) {
        return await taskmanAxios.post(API_MAKE_PROJECT_DOCUMENT_ARCHIVE_URL(projectId, documentId), {})
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async unarchive(projectId: string, documentId: string | number) {
        return await taskmanAxios.post(API_MAKE_PROJECT_DOCUMENT_UNARCHIVE_URL(projectId, documentId), {})
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async restore(projectId: string, documentId: string | number) {
        return await taskmanAxios.post(API_MAKE_PROJECT_DOCUMENT_RESTORE_URL(projectId, documentId), {})
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async delete(projectId: string, documentId: string | number) {
        return await taskmanAxios.delete(API_MAKE_PROJECT_DOCUMENT_URL(projectId, documentId))
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }
}
